type UserData = {
  patientId?: number
  email?: string
  name?: string
}

type SetupInsiderSdkParams = {
  userData: UserData
  allowCRM?: boolean
}

export const setupInsiderSdk = async (params: SetupInsiderSdkParams) => {
  const { allowCRM, userData } = params

  // TODO: when removing braze, rename the flag to PermiteEnvioCRM
  if (!userData?.patientId || !allowCRM) {
    removeInsiderScripts()
    return
  }

  initializeInsiderQueue(userData, true)
  loadInsiderScript(userData?.patientId)
}

const removeInsiderScripts = () => {
  const existingScript = document.getElementById('insiderTag')
  if (existingScript) {
    document.head.removeChild(existingScript)
  }
  const worker = document.getElementById('insider-worker')
  if (worker) {
    document.head.removeChild(worker)
  }
}

const initializeInsiderQueue = (userData: UserData, init: boolean) => {
  window.InsiderQueue = []
  window.InsiderQueue.push({
    type: 'user',
    value: {
      uuid: String(userData?.patientId),
      email: userData?.email,
      name: userData?.name,
      gdpr_optin: true,
    },
  })
  if (init) window.InsiderQueue.push({ type: 'init' })
}

const loadInsiderScript = (patientId: number) => {
  const existingScript = document.getElementById('insiderTag')
  if (!existingScript) {
    const script = document.createElement('script')
    script.id = 'insiderTag'
    script.setAttribute('data-patient', String(patientId))
    script.src = import.meta.env.VITE_APP_INSIDER_SCRIPT_URL || ''
    script.async = true
    document.head.appendChild(script)
    return
  }
  existingScript.setAttribute('data-patient', String(patientId))
  window.Insider?.eventManager?.dispatch('init-manager:re-initialize')
}

const buildEvent = (event: string, payload: unknown) => {
  const normalizedPayload = Object.entries(
    (payload ?? {}) as Record<string, unknown>,
  ).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [normalize(key)]: value,
    }),
    {},
  )

  return [
    {
      event_name: normalize(event),
      event_parameters: {
        custom: normalizedPayload,
      },
    },
  ]
}

export const sendInsiderCustomEvent = (event: string, payload: unknown) => {
  const insiderEvent = buildEvent(event, payload)
  if (window.Insider?.initialized) {
    window.InsiderQueue?.push({ type: 'custom_event', value: insiderEvent })
  } else {
    let retries = 0
    const schedule = setInterval(() => {
      if (window.Insider?.initialized) {
        window.InsiderQueue?.push({
          type: 'custom_event',
          value: insiderEvent,
        })
        clearInterval(schedule)
      }
      if (retries++ >= 5) {
        clearInterval(schedule)
      }
    }, 1000)
  }
}

export function sendPurchaseEventWithQueue(
  params: Purchase,
  userData: UserData,
) {
  const { order_id, total, items = [], currency } = params

  const [firstItem = {} as Product] = items
  const {
    image_url = null,
    name = null,
    id = null,
    quantity = null,
    taxonomy = null,
    unit_price = null,
    unit_sale_price = null,
    custom = {},
  } = firstItem

  const purchaseEvent = {
    type: 'purchase',
    value: {
      order_id: order_id?.toString(),
      total: total || 0,
      items: [
        {
          id: id?.toString(),
          name,
          taxonomy,
          unit_price,
          unit_sale_price,
          quantity,
          product_image_url: image_url,
          custom,
        },
      ],
    },
  }

  const currencyEvent = {
    type: 'currency',
    value: currency || 'BRL',
  }

  const initEvent = {
    type: 'init',
  }

  initializeInsiderQueue(userData, false)
  window.InsiderQueue.push(purchaseEvent, currencyEvent, initEvent)
  loadInsiderScript(userData?.patientId)
}

/**
 * Normalize a string according to the Insider requirements:
 * - should start with a letter
 * - every character should be in lower case
 * - no special symbols or spaces
 * - characters - and _ are allowed
 * - numbers are allowed
 *
 * @param input raw event or property name
 * @return cleaned up name that should be accepted by the insider api
 */
const normalize = (input: string): string => {
  const WHITE_SPACE_REGEX = /\s/g
  const NON_LATIN_REGEX = /[^\w-]/g
  const STARTS_WITH_NUMBER_REGEX = /^\d/
  const DIGIT_NAME_PREFIX = 'p_'

  const noWhitespace = input.trim().replace(WHITE_SPACE_REGEX, '_')
  const normalized = noWhitespace.normalize('NFD')
  let slug = normalized.replace(NON_LATIN_REGEX, '')

  if (STARTS_WITH_NUMBER_REGEX.test(slug.charAt(0))) {
    slug = `${DIGIT_NAME_PREFIX}${slug}`
  }

  return slug.toLowerCase()
}
